import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

/*
 * "SMS til aktive på trening" — admin drawer that invites everyone who has
 * been active at the club's trainings the last 6-12 months to this convention.
 * The recipient list comes from GET /api/conventions/:id/training-active-users
 * (participant entries + score rows at non-official conventions); sending goes
 * through the generic sendSms drawer, personalized with {{flettefelt}}.
 */

// Norwegian numbers reach us in several shapes: the API normalizes trainees to
// +47XXXXXXXX, while organizers are stored exactly as typed ("915 20 191",
// "+47 915 20 191", "0047..."). Reduce both to the 8 national digits so the two
// lists can be compared. Exported for unit testing.
function phoneKey(phone) {
    if (!phone) {
        return '';
    }
    let digits = String(phone).replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('0047')) {
        digits = digits.substring(4);
    } else if (digits.length === 10 && digits.startsWith('47')) {
        digits = digits.substring(2);
    }
    return digits;
}

// Predicate telling whether a training recipient is already an arrangør on this
// convention. Organizers added from the user search carry the user id, while
// organizers pasted from a list have id 0 — those only match on phone number.
// Exported for unit testing.
function buildOrganizerMatcher(organizers = []) {
    const ids = new Set();
    const phones = new Set();
    organizers.forEach(o => {
        if (Number(o?.id) > 0) {
            ids.add(Number(o.id));
        }
        const key = phoneKey(o?.cellphone);
        if (key) {
            phones.add(key);
        }
    });
    return (recipient) => {
        if (!recipient) {
            return false;
        }
        if (Number(recipient.id) > 0 && ids.has(Number(recipient.id))) {
            return true;
        }
        const key = phoneKey(recipient.cellphone);
        return key !== '' && phones.has(key);
    };
}

// Pure mapping from API recipients to the sendSms drawer's users[] shape.
// Exported for unit testing.
function buildTraineeSmsUsers(recipients = [], excluded = {}, conventionTitle = '') {
    return recipients
        .filter(r => r.cellphone && !excluded[r.id])
        .map(r => ({
            firstname: r.firstname || '',
            lastname: r.lastname || '',
            cellphone: r.cellphone,
            mergeFields: {
                fornavn: r.firstname || '',
                etternavn: r.lastname || '',
                stevne: conventionTitle || '',
            },
        }));
}

@observer
class ConventionViewTrainingSmsAdmin extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            months: 12,
            data: null,
            // Recipient selection tracked as EXCLUSIONS (user id -> true) so the
            // default (empty) means "all selected" — same pattern as the
            // organizer drawer.
            excluded: {},
            // Skip everyone who is already an arrangør on this convention, so the
            // invite only goes to shooters who are not part of it. Off by default.
            skipOrganizers: false,
        };
    }

    // Recipients shown and sent to: the API list minus the arrangører when the
    // skip toggle is on. Single source of truth for the table, the counts and
    // the send action.
    getVisibleRecipients = () => {
        const { data, skipOrganizers } = this.state;
        const recipients = data?.recipients || [];
        if (!skipOrganizers) {
            return recipients;
        }
        const { conventionStore } = this.props.stores;
        const isOrganizer = buildOrganizerMatcher(conventionStore.convention?.organizers);
        return recipients.filter(r => !isOrganizer(r));
    }

    toggleSkipOrganizers = () => {
        this.setState(prev => ({ skipOrganizers: !prev.skipOrganizers }));
    }

    componentDidMount() {
        this.load(this.state.months);
    }

    load = async (months) => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id) {
            this.setState({ loading: false, error: 'Fant ikke stevnet' });
            return;
        }
        this.setState({ loading: true, error: null });
        try {
            const response = await util.fetchApi(`/api/conventions/${convention.id}/training-active-users`, { method: 'GET' }, { months });
            if (response?.status === 200) {
                this.setState({ loading: false, data: response.data, excluded: {} });
            } else {
                this.setState({ loading: false, error: response?.message || 'Klarte ikke å hente listen' });
            }
        } catch (err) {
            this.setState({ loading: false, error: err.message || 'Klarte ikke å hente listen' });
        }
    }

    setMonths = (e) => {
        const months = parseInt(e.target.closest('button').dataset.months, 10);
        if (months === this.state.months) {
            return;
        }
        this.setState({ months });
        this.load(months);
    }

    toggleSelectRecipient = (e) => {
        const id = parseInt(e.target.dataset.id, 10);
        this.setState((prev) => {
            const excluded = { ...prev.excluded };
            if (excluded[id]) {
                delete excluded[id];
            } else {
                excluded[id] = true;
            }
            return { excluded };
        });
    }

    toggleSelectAll = () => {
        const { excluded } = this.state;
        const recipients = this.getVisibleRecipients();
        const allSelected = recipients.length > 0 && recipients.every(r => !excluded[r.id]);
        if (allSelected) {
            const newExcluded = {};
            recipients.forEach(r => { newExcluded[r.id] = true; });
            this.setState({ excluded: newExcluded });
        } else {
            this.setState({ excluded: {} });
        }
    }

    sendSmsToTrainees = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { excluded } = this.state;
        const { drawerHeightMediumLarge } = appState;

        const users = buildTraineeSmsUsers(this.getVisibleRecipients(), excluded, convention.title);
        if (users.length === 0) {
            return;
        }

        appState.openDrawer('sendSms', {
            height: drawerHeightMediumLarge,
            users,
            personalized: true,
            message: `Hei {{fornavn}}! Vi inviterer deg til {{stevne}}. Håper vi ser deg der! Hilsen ${convention.organizer || 'arrangøren'}`,
            from: convention.title || 'Stevne',
            // Provider-valid SMS sender (a-zA-Z0-9, max 11 chars); the title is
            // only a seed and is sanitized by the sendSms drawer.
            editableSender: true,
        }, drawerLevel + 1);
    }

    render() {
        const {
            loading, error, months, data, excluded, skipOrganizers,
        } = this.state;
        const { userStore, conventionStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const organizers = conventionStore.convention?.organizers || [];
        const isOrganizer = buildOrganizerMatcher(organizers);
        const allRecipients = data?.recipients || [];
        // How many of the training-active shooters are already arrangører here.
        const organizerCount = allRecipients.filter(isOrganizer).length;
        const recipients = this.getVisibleRecipients();
        const noPhone = data?.noPhone || [];
        const selectedCount = recipients.filter(r => !excluded[r.id]).length;
        const allSelected = recipients.length > 0 && selectedCount === recipients.length;
        const someSelected = selectedCount > 0 && !allSelected;

        return (<>
            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3 text-danger'>
                SMS til aktive på trening
            </div>
            <div class={`d-flex flex-column mt-0 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-auto border border-danger shadow`}>

                <div class='d-flex flex-row flex-wrap align-items-center mb-2'>
                    <span class='font-weight-lighter me-2'>Aktive siste:</span>
                    <div class='btn-group'>
                        <button
                            class={`btn btn-sm rounded-pill-left ${months === 6 ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={this.setMonths}
                            data-months='6'
                            disabled={loading}
                        >
                            6 måneder
                        </button>
                        <button
                            class={`btn btn-sm rounded-pill-right ${months === 12 ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={this.setMonths}
                            data-months='12'
                            disabled={loading}
                        >
                            12 måneder
                        </button>
                    </div>
                </div>
                <div class='font-weight-lighter text-body-secondary mb-2'>
                    <small>
                        Alle som har vært aktive på klubbens treninger i perioden — påmeldte og alle med registrerte skudd.
                        Approberte stevner regnes ikke med.
                    </small>
                </div>

                {organizers.length > 0 && <>
                    <div class='form-check mb-2'>
                        <input
                            type='checkbox'
                            class='form-check-input'
                            id='trainingSmsSkipOrganizers'
                            checked={skipOrganizers}
                            onChange={this.toggleSkipOrganizers}
                            disabled={loading}
                        />
                        <label class='form-check-label' for='trainingSmsSkipOrganizers'>
                            Hopp over arrangører ({organizerCount})
                            <small class='text-body-secondary ms-2'>
                                Send kun til de som ikke allerede er med på stevnet.
                            </small>
                        </label>
                    </div>
                </>}

                {loading && <>
                    <div class='text-center py-4'>
                        <i class='fa-duotone fa-spinner fa-spin me-2' />
                        Henter aktive skyttere...
                    </div>
                </>}

                {!loading && error && <>
                    <div class='alert alert-danger'>{error}</div>
                </>}

                {!loading && !error && data && <>
                    <div class='d-flex font-weight-lighter border-bottom mb-2 mt-2'>
                        {recipients.length} mottakere fra {data.trainingConventionCount} treninger:
                        {skipOrganizers && organizerCount > 0 && <small class='text-body-secondary ms-2'>
                            ({organizerCount} arrangører filtrert bort)
                        </small>}
                    </div>

                    {recipients.length > 0 ? <>
                        <div class='table-responsive'>
                            <table class='table table-sm table-striped table-hover'>
                                <thead>
                                    <tr>
                                        <th scope='col'>
                                            <input
                                                type='checkbox'
                                                class='form-check-input'
                                                checked={allSelected}
                                                ref={el => { if (el) el.indeterminate = someSelected; }}
                                                onChange={this.toggleSelectAll}
                                                title='Velg alle / ingen'
                                            />
                                        </th>
                                        <th scope='col'>Navn</th>
                                        <th scope='col'>Telefon</th>
                                        <th scope='col'>Sist aktiv</th>
                                        <th scope='col'>Treninger</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recipients.map(r => (
                                        <tr key={r.id}>
                                            <td>
                                                <input
                                                    type='checkbox'
                                                    class='form-check-input'
                                                    checked={!excluded[r.id]}
                                                    data-id={r.id}
                                                    onChange={this.toggleSelectRecipient}
                                                />
                                            </td>
                                            <td>
                                                <nobr>
                                                    {r.firstname} {r.lastname}
                                                    {r.sportsClub && <small class='text-body-secondary ms-2'>{r.sportsClub}</small>}
                                                    {isOrganizer(r) && <span class='badge bg-body-secondary text-body-secondary ms-2'>
                                                        Arrangør
                                                    </span>}
                                                </nobr>
                                            </td>
                                            <td><nobr>{r.cellphone}</nobr></td>
                                            <td><nobr>{r.lastActiveDate ? util.formatDate(r.lastActiveDate) : '-'}</nobr></td>
                                            <td>{r.trainingCount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div class='d-flex flex-wrap justify-content-center mt-2'>
                            <button
                                class='btn btn-outline-success rounded-pill m-1'
                                onClick={this.sendSmsToTrainees}
                                disabled={selectedCount === 0}
                            >
                                <i class='fa-duotone fa-message-sms me-2' />
                                Send SMS til valgte ({selectedCount})
                            </button>
                        </div>
                    </> : <>
                        <div class='text-muted text-center py-3'>
                            {skipOrganizers && allRecipients.length > 0
                                ? 'Alle de aktive på trening er allerede arrangører på stevnet.'
                                : 'Fant ingen aktive skyttere på treninger i perioden.'}
                        </div>
                    </>}

                    {noPhone.length > 0 && <>
                        <div class='d-flex font-weight-lighter border-bottom mb-2 mt-3'>
                            Mangler telefonnummer ({noPhone.length}):
                        </div>
                        <div class='text-body-secondary'>
                            <small>
                                {noPhone.map(p => `${p.firstname || ''} ${p.lastname || ''}`.trim() || `Bruker ${p.id}`).join(', ')}
                            </small>
                        </div>
                    </>}
                </>}
            </div>
        </>);
    }
}

export {
    ConventionViewTrainingSmsAdmin,
    buildTraineeSmsUsers,
    buildOrganizerMatcher,
    phoneKey,
};

export default ConventionViewTrainingSmsAdmin;
